import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../../services/api';
import './CruxFeatureTable.css';

/**
 * Section 15 of the CRUX, edited as the table it is.
 *
 * It was a textarea holding a markdown table, which meant hand-aligning pipes to
 * answer twenty-two yes/no questions — and a row typed slightly wrong is not a
 * syntax error, it is a feature that silently never counts. This edits the rows
 * and writes the same markdown back, so a document written in the repo and one
 * edited here stay the same document.
 *
 * The rows come from the backend rather than a copy here: the list is exactly
 * what the Paradigm feature usage score reads, and a row the scorer does not
 * know is a question nobody acts on.
 */
const STATES = [
  { key: 'yes', label: 'Yes' },
  { key: 'partial', label: 'Partial' },
  { key: 'no', label: 'No' },
];

/** {id: {using, reason}} from the stored markdown. */
export function parseTable(markdown) {
  const out = {};
  for (const line of String(markdown || '').split('\n')) {
    if ((line.match(/\|/g) || []).length < 3) continue;
    const cells = line.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());
    if (cells.length < 2) continue;
    const id = cells[0].replace(/`/g, '').trim();
    if (!id || id === 'Feature' || /^[-: ]+$/.test(id)) continue;
    const said = cells[1].toLowerCase();
    out[id] = {
      using: ['yes', 'no', 'partial'].includes(said) ? said : '',
      reason: (cells[2] || '').trim(),
    };
  }
  return out;
}

/** Back to markdown, in the shape the backend parses. */
export function toMarkdown(features, answers) {
  const rows = features.map(f => {
    const a = answers[f.id] || {};
    return `| \`${f.id}\` | ${a.using || '?'} | ${a.reason || ''} |`;
  });
  return [
    'One row per feature. A reason for **no** is as useful as a reason for yes —',
    'this table is read directly into the Paradigm feature usage score.',
    '',
    '| Feature | Using it | Why / why not |',
    '|---|---|---|',
    ...rows,
    '',
  ].join('\n');
}

function CruxFeatureTable({ value, onChange }) {
  const [features, setFeatures] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.request('/third-party/platform-features')
      .then(r => setFeatures(r?.features || []))
      .catch(e => setError(e.message || 'Could not load the feature list.'));
  }, []);

  const answers = useMemo(() => parseTable(value), [value]);

  /* Writes read from here, not from `answers`.
   *
   * Every edit rewrites the whole table from the current answers, and `answers`
   * is only as fresh as the last render. Clicking "yes" and then typing the
   * reason emitted a table built from the answers as they were *before* the
   * click — so the reason saved and the answer silently reverted to unanswered.
   * The ref moves forward the moment we emit, so the second edit builds on the
   * first whether or not React has re-rendered in between. */
  const live = useRef(answers);
  useEffect(() => { live.current = answers; }, [answers]);

  const set = (id, patch) => {
    if (!features) return;
    const next = { ...live.current, [id]: { ...(live.current[id] || {}), ...patch } };
    live.current = next;
    onChange(toMarkdown(features, next));
  };

  if (error) return <p className="ftable__msg ftable__msg--bad">{error}</p>;
  if (!features) return <p className="ftable__msg">Loading…</p>;

  const answered = features.filter(f => answers[f.id]?.using).length;

  return (
    <div className="ftable">
      <p className="ftable__count">
        <strong>{answered}</strong> of {features.length} answered
        {answered < features.length && <> · leaving one blank counts as unverified, not as a no</>}
      </p>

      <table>
        <thead>
          <tr>
            <th>Feature</th>
            <th className="ftable__using">Are you using it?</th>
            <th>Why / why not</th>
          </tr>
        </thead>
        <tbody>
          {features.map(f => {
            const a = answers[f.id] || {};
            return (
              <tr key={f.id} className={a.using ? '' : 'is-unanswered'}>
                <td>
                  <span className="ftable__label">{f.label}</span>
                  <code>{f.id}</code>
                  {/* The four the platform cannot observe: what is written here
                      is the only evidence there is. */}
                  {f.declared_only && <em title="Nothing else can tell us — this row is the only evidence">only you can say</em>}
                </td>
                <td className="ftable__using">
                  <div className="ftable__states" role="group" aria-label={`Using ${f.label}`}>
                    {STATES.map(s => (
                      <button
                        key={s.key}
                        type="button"
                        className={a.using === s.key ? `is-on is-${s.key}` : ''}
                        aria-pressed={a.using === s.key}
                        onClick={() => set(f.id, { using: a.using === s.key ? '' : s.key })}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </td>
                <td>
                  <input
                    type="text"
                    value={a.reason || ''}
                    onChange={e => set(f.id, { reason: e.target.value })}
                    placeholder={a.using === 'no' ? 'Why not?' : 'Why?'}
                    aria-label={`Reason for ${f.label}`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default CruxFeatureTable;
